import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { LevelBadge } from "@/components/logs/LevelBadge";
import { dateLocale, t } from "@/lib/i18n";
import type { LogEntry, LogResponse } from "@/lib/types";

export function TraceDetailPage() {
  const { traceId = "" } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LogEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    if (!traceId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<LogResponse>(`/api/trace/${encodeURIComponent(traceId)}`);
      setLogs(resp.logs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [traceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const summary = useMemo(() => {
    if (!logs || logs.length === 0) return null;
    const t0 = new Date(logs[0].timestamp).getTime();
    const tN = new Date(logs[logs.length - 1].timestamp).getTime();
    const services = Array.from(
      new Set(logs.map((l) => l.service ?? l.client_id).filter(Boolean) as string[]),
    );
    const errors = logs.filter((l) => l.level === "ERROR" || l.level === "FATAL").length;
    const warns = logs.filter((l) => l.level === "WARN").length;
    return { t0, tN, duration: tN - t0, services, errors, warns };
  }, [logs]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(traceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/traces")}
              aria-label={t("Zurück zur Liste")}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span>{t("Trace")}</span>
          </span>
        }
        description={traceId}
        actions={
          <>
            <button
              type="button"
              onClick={copyId}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted"
            >
              <Copy className="h-3 w-3" /> {copied ? t("Kopiert") : t("ID kopieren")}
            </button>
            <Link
              to={`/logs?trace=${encodeURIComponent(traceId)}`}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted"
            >
              <ExternalLink className="h-3 w-3" /> {t("In Logs öffnen")}
            </Link>
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
            >
              <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
              {t("Aktualisieren")}
            </button>
          </>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        {summary && logs ? (
          <>
            <Card>
              <div className="grid grid-cols-2 gap-4 text-xs sm:grid-cols-4">
                <Meta label={t("Einträge")}>{logs.length}</Meta>
                <Meta label={t("Dauer")}>{formatDuration(summary.duration)}</Meta>
                <Meta label={t("Errors")} tone={summary.errors > 0 ? "destructive" : "default"}>
                  {summary.errors}
                </Meta>
                <Meta label={t("Warnings")} tone={summary.warns > 0 ? "warning" : "default"}>
                  {summary.warns}
                </Meta>
                <Meta label={t("Beteiligte Services")}>
                  <div className="flex flex-wrap gap-1">
                    {summary.services.map((s) => (
                      <span
                        key={s}
                        className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </Meta>
                <Meta label={t("Beginn")}>
                  {new Date(summary.t0).toLocaleString(dateLocale())}
                </Meta>
              </div>
            </Card>

            <Card title={t("Timeline")} description={t("Chronologisch — die graue Linie zeigt die relative Position innerhalb des Traces.")}>
              <Timeline logs={logs} t0={summary.t0} duration={summary.duration} />
            </Card>
          </>
        ) : loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade Trace…")}</div>
        ) : (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("Keine Logs zu diesem Trace gefunden.")}
          </div>
        )}
      </div>
    </div>
  );
}

function Timeline({ logs, t0, duration }: { logs: LogEntry[]; t0: number; duration: number }) {
  return (
    <ol className="relative space-y-1 font-mono text-xs">
      {logs.map((log, i) => {
        const ts = new Date(log.timestamp).getTime();
        const offsetMs = ts - t0;
        const pct = duration > 0 ? (offsetMs / duration) * 100 : 0;
        return (
          <li key={log.id ?? i} className="grid grid-cols-[80px_64px_140px_1fr] items-start gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40">
            <div className="flex flex-col">
              <span className="text-muted-foreground tabular-nums">+{formatOffset(offsetMs)}</span>
              <div className="mt-1 h-0.5 rounded bg-border">
                <div
                  className="h-0.5 rounded bg-primary/60"
                  style={{ width: `${Math.max(pct, 0.5)}%` }}
                />
              </div>
            </div>
            <LevelBadge level={log.level} />
            <div className="min-w-0 truncate text-muted-foreground">
              {log.service ?? log.client_id}
              {log.host ? <span className="text-muted-foreground/60"> · {log.host}</span> : null}
            </div>
            <div className="min-w-0">
              <div className="break-words">{log.message}</div>
              {log.span_id ? (
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {t("span {id}", { id: log.span_id })}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Meta({
  label,
  children,
  tone = "default",
}: {
  label: string;
  children: React.ReactNode;
  tone?: "default" | "destructive" | "warning";
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-warning"
        : "text-foreground";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={"mt-1 text-sm font-medium " + toneClass}>{children}</div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  return `${(ms / 60_000).toFixed(2)} min`;
}

function formatOffset(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(3)} s`;
  return `${(ms / 60_000).toFixed(2)} min`;
}
